import { Component, inject, signal, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerScanOrientation,
  CapacitorBarcodeScannerTypeHint,
} from '@capacitor/barcode-scanner';
import { Capacitor } from '@capacitor/core';
import { CodeReaderService } from '@features/code-readers/code-reader.service';
import { PortalButtonDirective } from '../../../portal-button/portal-button.directive';
import { PortalInputComponent } from '../../../portal-input/portal-input.component';
import { PortalPanelComponent } from '../../../portal-panel/portal-panel.component';
import { ACTIVATION_SERVICE } from '../activation.service.interface';

@Component({
  imports: [FormsModule, ReactiveFormsModule, PortalPanelComponent, PortalInputComponent, PortalButtonDirective],
  templateUrl: './player-group-select.component.html',
  styleUrl: './player-group-select.component.css',
})
export class PlayerGroupSelectComponent {
  protected showSuccessMessage = signal(false);
  protected form = new FormGroup({
    code: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(14), Validators.maxLength(14)],
    }),
  });

  private activationService = inject(ACTIVATION_SERVICE);
  private router = inject(Router);
  private codeReaderService = inject(CodeReaderService);
  private vcr = inject(ViewContainerRef);

  constructor() {
    this.activationService.deletePlayerGroupCode();
  }

  async onSubmit() {
    if (!this.form.valid) {
      throw new Error('Invalid form');
    }

    await this.setCode(this.form.value.code!);
  }

  async scanQrCode(): Promise<boolean> {
    // check with capactitor on what platform we are running
    const isMobile = Capacitor.isNativePlatform();
    if (isMobile) {
      try {
        const { ScanResult: qr } = await CapacitorBarcodeScanner.scanBarcode({
          hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
          scanOrientation: CapacitorBarcodeScannerScanOrientation.LANDSCAPE,
        });

        const regex = /\d{4}-?\d{4}-?\d{4}/g;

        if (!regex.test(qr)) {
          return await this.scanQrCode();
        }

        await this.setCode(qr);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    } else {
      try {
        try {
          // Ensure camera access is granted
          await this.codeReaderService.requestCameraAccess();
        } catch (error) {
          window.alert('Camera access denied or unavailable. Check your permissions and try again.');
        }
        const qr = await this.codeReaderService.readQR(this.vcr);
        const regex = /\d{4}-?\d{4}-?\d{4}/g;

        if (!regex.test(qr.data)) {
          return await this.scanQrCode();
        }

        await this.setCode(qr.data);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    }
  }

  private async setCode(code: string) {
    // todo: some sort of check to see if the code is valid
    this.activationService.setPlayerGroupCode(code);

    // Show success message with animation
    this.showSuccessMessage.set(true);

    // Wait 2 seconds before navigating
    setTimeout(async () => {
      await this.router.navigate(['/select-profile']);
    }, 2000);
  }
}
